import { type CSSProperties } from 'react';

/**
 * ButtonSparkles — a field of tiny GOLD-LEAF FLAKES that catch the light around
 * a button on hover. Not uniform "sparkle" stars: irregular torn/angular shapes
 * and small specks, each tumbling (rotating) while it flashes from deep gold to
 * bright and back — the flash is the specular catch that makes a metal flake
 * read as reflecting, not a flat glyph. Driven by CSS (`.btn-sparkles`/`.spark`
 * in globals.css); animates on the parent's :hover, stilled under reduced-motion.
 * Render as the last child of a `.btn-primary`/`.btn-secondary` (which must NOT
 * clip overflow, so flakes can drift past the edge).
 */

// Irregular gold-leaf shapes (viewBox 0 0 16 16) — torn quad, slanted sliver,
// triangle flake, irregular pentagon, tiny speck. Varied so it never looks like
// a row of identical stars. Exported so the global injector (GlobalSparkles)
// can build the same field as plain DOM for buttons that aren't this component.
export const SHAPES: readonly string[] = [
  'M2 5 L11 2 L14 9 L5 13 Z',
  'M3 8 L13 4 L14 7 L4 11 Z',
  'M8 2 L14 12 L3 11 Z',
  'M7 1 L13 4 L12 11 L4 12 L2 6 Z',
  'M6 3 L9 6 L6 9 L3 6 Z',
];

// x/y in % around the perimeter (some past the edge); s = px size; r = base
// rotation; d = animation delay; k = shape index. Each flake drifts OUTWARD from
// the button centre as it glints (computed below), so they float off the edge.
export const FLAKES: ReadonlyArray<{ x: number; y: number; s: number; r: number; d: number; k: number }> = [
  { x: 1, y: 24, s: 11, r: 12, d: 0.0, k: 0 },
  { x: -5, y: 62, s: 7, r: 40, d: 0.5, k: 1 },
  { x: 12, y: 108, s: 9, r: 70, d: 0.9, k: 2 },
  { x: 44, y: 116, s: 10, r: 20, d: 0.3, k: 3 },
  { x: 78, y: 110, s: 8, r: 55, d: 0.7, k: 1 },
  { x: 105, y: 56, s: 11, r: 30, d: 0.15, k: 0 },
  { x: 99, y: 8, s: 9, r: 80, d: 0.62, k: 3 },
  { x: 66, y: -14, s: 10, r: 10, d: 0.42, k: 2 },
  { x: 28, y: -14, s: 8, r: 60, d: 1.0, k: 1 },
  { x: -7, y: 40, s: 6, r: 25, d: 1.2, k: 4 },
  { x: 107, y: 32, s: 6, r: 50, d: 0.85, k: 4 },
  { x: 90, y: 96, s: 5, r: 0, d: 1.35, k: 4 },
];

export function ButtonSparkles() {
  return (
    <span className="btn-sparkles" aria-hidden="true">
      {FLAKES.map((f, i) => {
        // Outward drift direction = away from the button centre (50%, 50%).
        const cx = f.x - 50;
        const cy = f.y - 50;
        const len = Math.hypot(cx, cy) || 1;
        const drift = 14;
        const style = {
          left: `${f.x}%`,
          top: `${f.y}%`,
          width: f.s,
          height: f.s,
          animationDelay: `${f.d}s`,
          '--r': `${f.r}deg`,
          '--dx': `${((cx / len) * drift).toFixed(1)}px`,
          '--dy': `${((cy / len) * drift).toFixed(1)}px`,
        } as CSSProperties;
        return (
          <svg key={i} className="spark" viewBox="0 0 16 16" style={style}>
            <path d={SHAPES[f.k]} />
          </svg>
        );
      })}
    </span>
  );
}
